import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  UploadCloud,
  File as FileIcon,
  X,
  Copy,
  Check,
  Clock,
  QrCode,
  ExternalLink,
  Plus,
} from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { cn, getErrorMessage } from "../lib/utils";
import { formatBytes, formatTimeLeft, formatAbsolute } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB
const MAX_FILES = 5;

const EXPIRY_OPTIONS = [
  { label: "1 hour", hours: 1 },
  { label: "6 hours", hours: 6 },
  { label: "1 day", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "7 days", hours: 168 },
];

const HomePage = () => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [expiryHours, setExpiryHours] = useState(24);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [results, setResults] = useState(null);

  const addFiles = useCallback(
    (incoming) => {
      const accepted = [];
      for (const file of incoming) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`"${file.name}" is larger than 5 GB and was skipped.`);
          continue;
        }
        accepted.push(file);
      }
      setFiles((prev) => {
        const merged = [...prev];
        for (const file of accepted) {
          if (
            merged.some((f) => f.name === file.name && f.size === file.size)
          ) {
            continue;
          }
          merged.push(file);
        }
        if (merged.length > MAX_FILES) {
          toast.error(`You can upload at most ${MAX_FILES} files at once.`);
          return merged.slice(0, MAX_FILES);
        }
        return merged;
      });
    },
    []
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    addFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("Add at least one file first.");
      return;
    }
    if (files.length > MAX_FILES) {
      toast.error(`You can upload at most ${MAX_FILES} files at once.`);
      return;
    }

    setUploading(true);
    setProgress({});

    try {
      const uploaded = await Promise.all(
        files.map(async (file, index) => {
          const formData = new FormData();
          formData.append("files", file);
          formData.append("expiry_hours", expiryHours);

          const response = await axios.post(`${API}/upload`, formData, {
            onUploadProgress: (event) => {
              const percent = event.total
                ? Math.round((event.loaded / event.total) * 100)
                : 0;
              setProgress((prev) => ({ ...prev, [index]: percent }));
            },
          });
          return response.data[0];
        })
      );

      setResults(uploaded);
      setFiles([]);
      setProgress({});
      toast.success(
        uploaded.length === 1
          ? "File uploaded — your link is ready!"
          : `${uploaded.length} files uploaded — your links are ready!`
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "Upload failed. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setResults(null);
    setFiles([]);
    setProgress({});
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 sm:px-6 sm:py-14">
        {/* Hero */}
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            Share files in seconds
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
            No accounts, no hassle. Drop a file, pick how long it stays online,
            and get a short link to share with anyone.
          </p>
        </div>

        {results ? (
          <ResultsView results={results} onReset={reset} />
        ) : (
          <Card className="rounded-2xl border-border/70 shadow-xl shadow-primary/5">
            <CardContent className="p-6 sm:p-8">
              {/* Drop zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => !uploading && inputRef.current?.click()}
                className={cn(
                  "group flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-14 text-center transition-all duration-200",
                  isDragging
                    ? "scale-[1.01] border-primary bg-primary/10"
                    : "border-border hover:border-primary/50 hover:bg-muted/50",
                  uploading && "pointer-events-none opacity-60"
                )}
              >
                <div
                  className={cn(
                    "mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-transform duration-200",
                    isDragging ? "scale-110" : "group-hover:scale-105"
                  )}
                >
                  <UploadCloud className="h-8 w-8" />
                </div>
                <p className="text-lg font-medium">
                  {isDragging ? "Drop files to add them" : "Drag & drop files here"}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  up to {MAX_FILES} files, 5 GB each
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-5 rounded-full px-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    inputRef.current?.click();
                  }}
                  disabled={uploading}
                >
                  Choose files
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files));
                    e.target.value = "";
                  }}
                />
              </div>

              {/* Selected files */}
              {files.length > 0 && (
                <ul className="mt-6 space-y-2">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${file.size}`}
                      className="flex items-center gap-3 rounded-xl border border-border/70 bg-muted/40 px-4 py-3"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FileIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatBytes(file.size)}
                        </p>
                        {uploading && (
                          <div className="mt-2 flex items-center gap-2">
                            <Progress
                              value={progress[index] || 0}
                              className="h-1.5"
                            />
                            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                              {progress[index] || 0}%
                            </span>
                          </div>
                        )}
                      </div>
                      {!uploading && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:text-destructive"
                          onClick={() => removeFile(index)}
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {/* Expiry selector */}
              <div className="mt-6">
                <p className="mb-2 flex items-center gap-1.5 text-sm font-medium">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Keep files online for
                </p>
                <div className="grid grid-cols-5 gap-1 rounded-xl bg-muted p-1">
                  {EXPIRY_OPTIONS.map((option) => (
                    <button
                      key={option.hours}
                      type="button"
                      disabled={uploading}
                      onClick={() => setExpiryHours(option.hours)}
                      className={cn(
                        "rounded-lg px-1 py-2 text-xs font-medium transition-all sm:text-sm",
                        expiryHours === option.hours
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Upload button */}
              <Button
                className="mt-6 h-12 w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/20"
                onClick={handleUpload}
                disabled={uploading || files.length === 0}
              >
                {uploading
                  ? "Uploading..."
                  : files.length > 1
                    ? `Upload ${files.length} files`
                    : "Upload"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

const ResultsView = ({ results, onReset }) => {
  return (
    <div className="space-y-4">
      {results.map((file) => (
        <ResultCard key={file.slug} file={file} />
      ))}
      <Button
        variant="outline"
        className="h-11 w-full rounded-xl"
        onClick={onReset}
      >
        <Plus className="mr-2 h-4 w-4" />
        Upload more files
      </Button>
    </div>
  );
};

const ResultCard = ({ file }) => {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(file.share_url);
    } catch {
      // Fallback for non-secure contexts
      const textarea = document.createElement("textarea");
      textarea.value = file.share_url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="rounded-2xl border-border/70 shadow-xl shadow-primary/5">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FileIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{file.original_filename}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatBytes(file.size)} · expires in{" "}
              {formatTimeLeft(file.expires_at)} (
              {formatAbsolute(file.expires_at)})
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <div className="min-w-0 flex-1 truncate rounded-xl border border-border/70 bg-muted/40 px-4 py-2.5 font-mono text-sm">
            {file.share_url}
          </div>
          <Button
            size="icon"
            variant="secondary"
            className="h-10 w-10 shrink-0 rounded-xl"
            onClick={copyLink}
            aria-label="Copy link"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="h-10 w-10 shrink-0 rounded-xl"
            onClick={() => setShowQr((prev) => !prev)}
            aria-label="Show QR code"
          >
            <QrCode className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="secondary"
            className="h-10 w-10 shrink-0 rounded-xl"
            asChild
          >
            <a
              href={file.share_url}
              target="_blank"
              rel="noreferrer"
              aria-label="Open link"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>

        {showQr && (
          <div className="mt-4 flex justify-center rounded-xl border border-border/70 bg-white p-4">
            <QRCodeSVG value={file.share_url} size={168} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HomePage;
